import React, { useEffect, useState } from 'react';
import { CheckCircle, MessageSquare, PhoneCall, ExternalLink } from 'lucide-react';
import Header from "../../components/header";
import styled from 'styled-components';
import {
  getCurrentSubscription,
  formatValidityDate,
  calculateDaysRemaining,
  Subscription
} from '../../services/subscription';

const S = {
  PageWrapper: styled.div`
    min-height: 100vh;
    background: #f9fafb;
    display: flex;
    flex-direction: column;
    margin-left: 220px;
    width: calc(100% - 220px);
    
    @media (max-width: 768px) {
      margin-left: 0;
      width: 100%;
    }
  `,

  HeaderSection: styled.div`
    background: #2563eb;
    padding: 2rem 1.5rem;
    color: white;
    text-align: center;
    
    .header-title {
      font-size: 1.875rem;
      font-weight: 700;
      margin-bottom: 1rem;
      color: white;
    }
    
    .current-plan {
      font-size: 1rem;
      margin-bottom: 1.5rem;
    }
  `,

  PlanToggle: styled.div`
    display: inline-flex;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 9999px;
    padding: 0.25rem;
    margin-bottom: 1rem;

    button {
      padding: 0.5rem 1.5rem;
      border: none;
      background: transparent;
      color: white;
      border-radius: 9999px;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;

      &.active {
        background: white;
        color: #2563eb;
      }

      .save-tag {
        background: #10b981;
        padding: 0.125rem 0.5rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        margin-left: 0.5rem;
        color: white;
      }
    }
  `,

  Container: styled.div`
    max-width: 72rem;
    margin: 0 auto;
    padding: 2rem 1rem;
    width: 100%;
  `,

  PlansWrapper: styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
  `,

  PlanCard: styled.div<{ highlighted?: boolean }>`
    background: white;
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    height: 100%;
    border: 1px solid ${props => props.highlighted ? '#3b82f6' : '#e5e7eb'};
    ${props => props.highlighted && 'box-shadow: 0 0 0 2px #3b82f6;'}
    
    transition: transform 0.2s, box-shadow 0.2s;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }
  `,

  PlanContent: styled.div`
    padding: 1.5rem;
    
    .plan-name {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 0.5rem;
    }
    
    .plan-desc {
      color: #6b7280;
      font-size: 0.875rem;
      margin-bottom: 1.5rem;
    }
  `,

  SupportTerms: styled.div`
    margin-bottom: 1.5rem;
    
    h3 {
      font-weight: 500;
      color: #4b5563;
      margin-bottom: 0.75rem;
      font-size: 0.875rem;
    }
    
    ul {
      list-style: none;
      padding: 0;
      
      li {
        display: flex;
        align-items: center;
        color: #6b7280;
        font-size: 0.875rem;
        margin-bottom: 0.5rem;
        
        .bullet {
          margin-right: 0.5rem;
          color: #9ca3af;
        }
      }
    }
  `,

  PlanPrice: styled.div`
    margin: 1.5rem 0;
    
    .price-amount {
      font-size: 1.875rem;
      font-weight: 700;
      color: #1f2937;
    }
    
    .price-period {
      font-size: 0.875rem;
      color: #6b7280;
    }
  `,

  FeaturesList: styled.ul`
    list-style: none;
    padding: 0;
    margin-bottom: 1.5rem;
    
    li {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1rem;
      color: #4b5563;
      font-size: 0.875rem;
    }
    
    .feature-icon {
      color: #3b82f6;
      margin-right: 0.75rem;
      flex-shrink: 0;
      margin-top: 0.125rem;
    }
  `,

  ActionButton: styled.button`
    width: 100%;
    padding: 0.75rem 1rem;
    border: none;
    background: #2563eb;
    color: white;
    font-weight: 500;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    
    &:hover {
      background: #1d4ed8;
    }
    
    .button-icon {
      margin-right: 0.5rem;
    }
  `,

  BottomCTAs: styled.div`
    margin-top: 3rem;
    text-align: center;
    
    .compare-link {
      color: #2563eb;
      font-weight: 500;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      margin-bottom: 1.5rem;
      
      &:hover {
        color: #1d4ed8;
        text-decoration: underline;
      }
    }
  `
};

const SubscriptionPage = () => {
  const [planType, setPlanType] = useState('quarterly');
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Premium+ pricing
  const premiumQuarterlyPrice = 1000;
  const premiumYearlyPrice = 2000; // 50% savings 
  
  // Ultimate pricing
  const ultimateQuarterlyPrice = 1500;
  const ultimateYearlyPrice = 3000; // 50% savings
  
  useEffect(() => {
    fetchSubscription();
  }, []);
  
  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await getCurrentSubscription();
      setCurrentSubscription(response.data.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Get current plan details
  const currentPlanDetails = {
    status: currentSubscription?.subscriptionPlan?.planType || 'free',
    isTrail: currentSubscription?.isTrail || false,
    quantity: currentSubscription?.quantity || 1,
    daysRemaining: currentSubscription ? calculateDaysRemaining(currentSubscription) : 0
  };

  return (
    <>
     
      <Header />
      <S.PageWrapper>
        <S.HeaderSection>
          <h1 className="header-title">Get started with our affordable plans</h1>
          
          {currentSubscription && currentSubscription.status !== 'free' && (
            <div className="current-plan">
              You are currently on {currentPlanDetails.status} plan
              {currentPlanDetails.daysRemaining > 0 && (
                <>
                  {` for ${currentPlanDetails.quantity} users, expiring in ${currentPlanDetails.daysRemaining} days `}
                  <span style={{ fontSize: '12px', opacity: 0.8 }}>
                    ({formatValidityDate(currentSubscription)})
                  </span>
                </>
              )}
              {currentPlanDetails.daysRemaining === 0 && ' (Expired)'}
            </div>
          )}
          
          <S.PlanToggle>
            <button 
              className={planType === 'quarterly' ? 'active' : ''} 
              onClick={() => setPlanType('quarterly')}
            >
              Quarterly
            </button>
            <button 
              className={planType === 'yearly' ? 'active' : ''} 
              onClick={() => setPlanType('yearly')}
            >
              Yearly
              <span className="save-tag">Save 50%</span>
            </button>
          </S.PlanToggle>
        </S.HeaderSection>

        <S.Container>
          <S.PlansWrapper>
            {/* Premium+ Plan */}
            <S.PlanCard>
              <S.PlanContent>
                <h2 className="plan-name">Premium+</h2>
                <p className="plan-desc">Great for business managing teams and advertises online</p>
                
                <S.SupportTerms>
                  <h3>Support terms</h3>
                  <ul>
                    <li><span className="bullet">•</span> WhatsApp support</li>
                    <li><span className="bullet">•</span> 24-48 hours SLA</li>
                  </ul>
                </S.SupportTerms>
                
                <S.PlanPrice>
                  <span className="price-amount">₹{planType === 'quarterly' ? premiumQuarterlyPrice : premiumYearlyPrice}</span>
                  <span className="price-period">/user{planType === 'yearly' ? '/year' : '/quarter'}</span>
                </S.PlanPrice>
                
                <S.FeaturesList>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Unlimited Leads</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Auto-sync from 20+ Sources</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Lead Form & Bulk Import</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Google Sheets & Contacts Sync</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Dashboard & Reports</span>
                  </li>
                </S.FeaturesList>
                
                <S.ActionButton 
                  as="a"
                  href="https://calendly.com/3sigmacrm/15min?utm_source=subscriptionpage&utm_medium=website&utm_campaign=organic&back=1&month=2025-05"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PhoneCall className="button-icon" size={16} />
                  Book Demo
                </S.ActionButton>
              </S.PlanContent>
            </S.PlanCard>
            
            {/* Ultimate Plan */}
            <S.PlanCard highlighted>
              <S.PlanContent>
                <h2 className="plan-name">Ultimate</h2>
                <p className="plan-desc">For power users that are looking to automate their sales and grow faster</p>
                
                <S.SupportTerms>
                  <h3>Support terms</h3>
                  <ul>
                    <li><span className="bullet">•</span> WhatsApp support</li>
                    <li><span className="bullet">•</span> 4-8 hours SLA</li>
                    <li><span className="bullet">•</span> Dedicated call support at ₹15,000 p.m</li>
                  </ul>
                </S.SupportTerms>
                
                <S.PlanPrice>
                  <span className="price-amount">₹{planType === 'quarterly' ? ultimateQuarterlyPrice : ultimateYearlyPrice}</span>
                  <span className="price-period">/user{planType === 'yearly' ? '/year' : '/quarter'}</span>
                </S.PlanPrice>
                
                <S.FeaturesList>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>WhatsApp Business API</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Email & WhatsApp Campaigns</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Orders, Invoices & Quotes</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>Advanced Automations</span>
                  </li>
                  <li>
                    <CheckCircle className="feature-icon" size={16} />
                    <span>iOS App Access</span>
                  </li>
                </S.FeaturesList>
                
                <S.ActionButton 
                  as="a"
                  href="https://calendly.com/3sigmacrm/15min?utm_source=subscriptionpage&utm_medium=website&utm_campaign=organic&back=1&month=2025-05"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PhoneCall className="button-icon" size={16} />
                  Book Demo
                </S.ActionButton>
              </S.PlanContent>
            </S.PlanCard>
          </S.PlansWrapper>
          
          <S.BottomCTAs>
            <a 
              href="https://www.3sigmacrm.com/pricing" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="compare-link"
            >
              Compare plan features <ExternalLink size={14} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: '4px' }} />
            </a>
            
            <div>
              <S.ActionButton 
                as="a" 
                href="https://wa.me/8814048362?text=I'm%20interested%20in%20learning%20more%20about%20your%20subscription%20plans"

                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', marginTop: '1rem' }}
              >
                Talk to our team<ExternalLink size={16} style={{ marginLeft: '8px' }} />
              </S.ActionButton>
            </div>
          </S.BottomCTAs>
        </S.Container>
      </S.PageWrapper>
     
      
    </>
  );
};

export default SubscriptionPage;